import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SportBadge } from '@/components/SportBadge';
import { CoachBadge } from '@/components/CoachBadge';
import { 
  ChevronRight, Calendar, Clock, MapPin, GraduationCap, 
  CalendarCheck, BookOpen 
} from 'lucide-react';
import { format, isToday, isTomorrow, differenceInDays } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { Sport } from '@/types';

interface UpcomingSession {
  id: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  sessionNumber: number;
  className: string;
  classSport: Sport;
  classLocation: string;
  coachName: string;
  coachAvatar: string | null;
  coachIsVerified: boolean;
  enrollmentId: string;
  classId: string;
}

interface UpcomingSessionsSectionProps {
  limit?: number;
}

export function UpcomingSessionsSection({ limit = 3 }: UpcomingSessionsSectionProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  const { data: upcomingSessions = [], isLoading } = useQuery({
    queryKey: ['upcoming-sessions', profile?.id],
    queryFn: async (): Promise<UpcomingSession[]> => {
      if (!profile?.id) return [];
      
      const { data, error } = await supabase
        .from('class_session_history')
        .select(`
          id,
          session_date,
          start_time,
          end_time,
          session_number,
          enrollment:class_enrollments!class_session_history_enrollment_id_fkey(
            id,
            class:training_classes(
              id,
              title,
              sport,
              location,
              coach:profiles!training_classes_coach_id_fkey(
                id, name, avatar,
                coach_profile:coach_profiles(is_verified)
              )
            )
          )
        `)
        .eq('student_id', profile.id)
        .eq('status', 'scheduled')
        .gte('session_date', format(new Date(), 'yyyy-MM-dd'))
        .order('session_date', { ascending: true })
        .order('start_time', { ascending: true })
        .limit(limit + 2); // Get a few more to filter
      
      if (error) {
        console.error('Error fetching upcoming sessions:', error);
        return [];
      }
      
      return (data || []).slice(0, limit).map((session: any) => ({
        id: session.id,
        sessionDate: session.session_date,
        startTime: session.start_time,
        endTime: session.end_time,
        sessionNumber: session.session_number,
        className: session.enrollment?.class?.title ?? 'Lớp học',
        classSport: session.enrollment?.class?.sport as Sport,
        classLocation: session.enrollment?.class?.location ?? '',
        coachName: session.enrollment?.class?.coach?.name ?? 'HLV',
        coachAvatar: session.enrollment?.class?.coach?.avatar,
        coachIsVerified: session.enrollment?.class?.coach?.coach_profile?.is_verified ?? false,
        enrollmentId: session.enrollment?.id,
        classId: session.enrollment?.class?.id,
      }));
    },
    enabled: !!profile?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
  
  if (isLoading) {
    return (
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-foreground">Buổi học sắp tới</h3>
          </div>
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse bg-muted/50 rounded-xl h-24" />
          ))}
        </div>
      </section>
    );
  }
  
  if (upcomingSessions.length === 0) {
    return null; // Don't show section if no upcoming sessions
  }
  
  const getDateLabel = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Hôm nay';
    if (isTomorrow(date)) return 'Ngày mai';
    const daysUntil = differenceInDays(date, new Date());
    if (daysUntil <= 7) return format(date, 'EEEE', { locale: vi });
    return format(date, 'dd/MM', { locale: vi });
  };
  
  const getDateBadgeVariant = (dateStr: string): 'default' | 'secondary' | 'outline' => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'default';
    if (isTomorrow(date)) return 'secondary';
    return 'outline';
  };
  
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20">
            <CalendarCheck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-bold text-foreground">Buổi học sắp tới</h3>
            <p className="text-xs text-muted-foreground">
              {upcomingSessions.length} buổi trong lịch
            </p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-muted-foreground hover:text-primary h-8 px-2"
          onClick={() => navigate('/my-training-schedule')}
        >
          Xem tất cả
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="space-y-2">
        {upcomingSessions.map((session) => (
          <Card 
            key={session.id}
            className="cursor-pointer hover:bg-muted/50 transition-colors border-l-4 border-l-primary"
            onClick={() => navigate(`/classes/${session.classId}`)}
          >
            <CardContent className="p-3">
              <div className="flex items-start gap-3">
                {/* Date indicator */}
                <div className="flex flex-col items-center min-w-[60px]">
                  <Badge variant={getDateBadgeVariant(session.sessionDate)} className="text-xs mb-1">
                    {getDateLabel(session.sessionDate)}
                  </Badge>
                  <div className="text-sm font-semibold text-foreground">
                    {session.startTime.slice(0, 5)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    - {session.endTime.slice(0, 5)}
                  </div>
                </div>
                
                {/* Session details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <SportBadge sport={session.classSport} size="sm" />
                    <Badge variant="outline" className="text-xs">
                      <BookOpen className="w-3 h-3 mr-1" />
                      Buổi {session.sessionNumber}
                    </Badge>
                  </div>
                  
                  <h4 className="font-medium text-sm text-foreground truncate mb-1">
                    {session.className}
                  </h4>
                  
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Avatar className="h-4 w-4">
                        <AvatarImage src={session.coachAvatar || undefined} />
                        <AvatarFallback className="text-[8px]">
                          {session.coachName[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span>{session.coachName}</span>
                      {session.coachIsVerified && (
                        <CoachBadge isVerified={true} size="sm" />
                      )}
                    </div>
                  </div>
                  
                  {session.classLocation && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{session.classLocation}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
